/**
 * TRAC-1006 — the customer's attempt to reach a person, remembered across pages.
 *
 * The chat panel is rebuilt on every tirerack.com navigation. Until now the only
 * record of a handoff on the page was React state in ChatInterface, so the first
 * render of the next page believed there was no handoff: the outage screen's gate
 * saw `idle` and not-dismissed, the first announcement poll put the screen back,
 * and it covered a live rep (and disabled typing). The one call that restores the
 * handoff gave up silently on any failure, which during an outage is the norm.
 *
 * This record is what the next page reads, synchronously, before anything else
 * can decide. It is per conversation (keyed by session id) and per tab
 * (`sessionStorage`): a navigation keeps it, Clear conversation invalidates it,
 * and a new tab starts without it and relies on the resume check.
 *
 * Extending it (TRAC-1007 and later): add an optional field to `HandoffAttempt`,
 * read it defensively in `readHandoffAttempt`, and keep VERSION unless an existing
 * field changes meaning. A record this code cannot read is treated as absent,
 * which is the pre-TRAC-1006 behaviour — never worse than before.
 *
 * Pure: storage is passed in. RealChatAgentService binds the key and session id.
 */
import type { HandoffState } from '../components/chat/chatBanner';
import { isHandoffInitiator, type HandoffInitiator } from './handoffInitiator';

export interface HandoffAttempt {
  /** The conversation this attempt belongs to. A different session never inherits it. */
  sessionId: string;
  /** Last known handoff state on the page. */
  state: HandoffState;
  /** The team menu is on screen and unanswered. */
  routingPending: boolean;
  /**
   * The outage screen was dismissed BY taking its route to a person. Holds for
   * the whole handoff, and past its end until the backend confirms it is over.
   */
  overlayDismissed: boolean;
  /**
   * TRAC-1007: the team the backend confirmed for this attempt, so the slow-wait message on
   * a later page names the right phone extension. Absent until the backend answers.
   */
  department?: string;
  /**
   * TRAC-1007: the ask (the funnel's `askedAt`, epoch ms) the slow-wait message was already
   * shown for. Keyed by the ask, so a new attempt is never mistaken for one already told.
   */
  slowNoticeFor?: number;
  /**
   * TRAC-1066: how far this attempt's escalate request got.
   *
   * `pressed` is written when the button is pressed and `connecting` goes on screen;
   * `sent` immediately before the request leaves the browser. They are not the same
   * moment: the request waits on initSession first, which took 1.3 s to 4.3 s on dev.
   * A navigation inside that gap leaves `pressed` behind, which is the next page's
   * proof that nobody was ever asked for this customer.
   *
   * Absent on a record written before TRAC-1066 (and on one written by a tab that had
   * no session id yet). Absent is read as "cannot prove it was never sent", which is
   * the pre-TRAC-1066 behaviour -- never worse than before.
   */
  escalateRequest?: 'pressed' | 'sent';
  /**
   * TRAC-1212: who started this attempt, so a page that re-sends an escalate the previous
   * page never got out still says who asked. Absent on older records (recorded as absent).
   */
  initiatedBy?: HandoffInitiator;
  /** Epoch ms of the last write. */
  updatedAt: number;
}

export type HandoffAttemptFields = Omit<HandoffAttempt, 'updatedAt'>;

export const HANDOFF_ATTEMPT_STORAGE_KEY = 'traci_handoff_attempt';

const VERSION = 1;
const STATES: readonly HandoffState[] = ['idle', 'connecting', 'queued', 'active', 'ended'];

const engaged = (s: HandoffState) => s === 'connecting' || s === 'queued' || s === 'active';

/**
 * Is there still a handoff to protect? True while connecting / queued / active,
 * and while a handoff dismissal is waiting for the backend to confirm the end.
 */
export function handoffAttemptOpen(attempt: HandoffAttempt | null): boolean {
  return !!attempt && (engaged(attempt.state) || attempt.overlayDismissed);
}

export function readHandoffAttempt(
  storage: Storage | null,
  key: string,
  sessionId: string | null,
): HandoffAttempt | null {
  if (!storage || !sessionId) return null;
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p || p.v !== VERSION || p.sessionId !== sessionId || !STATES.includes(p.state)) return null;
    return {
      sessionId: p.sessionId,
      state: p.state,
      routingPending: p.routingPending === true,
      overlayDismissed: p.overlayDismissed === true,
      department: typeof p.department === 'string' && p.department ? p.department : undefined,
      slowNoticeFor: typeof p.slowNoticeFor === 'number' ? p.slowNoticeFor : undefined,
      escalateRequest: p.escalateRequest === 'pressed' || p.escalateRequest === 'sent'
        ? p.escalateRequest : undefined,
      initiatedBy: isHandoffInitiator(p.initiatedBy) ? p.initiatedBy : undefined,
      updatedAt: typeof p.updatedAt === 'number' ? p.updatedAt : 0,
    };
  } catch {
    return null; // blocked storage or a corrupt record: behave as if nothing was saved
  }
}

export function writeHandoffAttempt(
  storage: Storage | null,
  key: string,
  fields: HandoffAttemptFields,
  now: number = Date.now(),
): void {
  if (!storage) return;
  try {
    const attempt: HandoffAttempt = { ...fields, updatedAt: now };
    if (!handoffAttemptOpen(attempt) && !attempt.routingPending) {
      storage.removeItem(key); // nothing left to remember
      return;
    }
    storage.setItem(key, JSON.stringify({ v: VERSION, ...attempt }));
  } catch {
    /* blocked storage / quota — the page still works, it just forgets on navigation */
  }
}

/** Backoff for the resume check: 1, 2, 4, 8 s. */
export const RESUME_BACKOFF_MS: readonly number[] = [1000, 2000, 4000, 8000];
/** After the backoff, while a handoff is still open. */
export const RESUME_STEADY_MS = 15000;

/**
 * How long to wait after the `failures`-th consecutive failed resume check
 * (0-based) before asking again — or null to give up.
 *
 * The backoff always runs, so a tab with no record (a second tab on the same
 * conversation) still gets a fair chance. Past it, asking continues every 15 s
 * only while something says a handoff is open; otherwise a customer who was
 * never in one is not polling a dead endpoint all afternoon.
 */
export function resumeRetryDelayMs(failures: number, stillOpen: boolean): number | null {
  if (failures < RESUME_BACKOFF_MS.length) return RESUME_BACKOFF_MS[failures];
  return stillOpen ? RESUME_STEADY_MS : null;
}
